import Vue from 'vue';
import API from '@/api';
import * as types from '@/store/mutationTypes';

// the status is a map of { String, Boolean }: company ID to loading status
const state = {
  customersStatus: {},
  customers: {},
  accounts: {},
  accountsStatus: {},
  vendors: {},
  vendorsStatus: {},
  salesTerms: {},
  salesTermsStatus: {},
  salesItems: {},
  salesItemsStatus: {},
  taxCodes: {},
  taxCodesStatus: {}
};

const getters = {
  customersStatus: qbState => qbState.customersStatus,
  customers: qbState => qbState.customers,
  accounts: qbState => qbState.accounts,
  accountsStatus: qbState => qbState.accountsStatus,
  vendors: qbState => qbState.vendors,
  vendorsStatus: qbState => qbState.vendorsStatus,
  salesTerms: qbState => qbState.salesTerms,
  salesTermsStatus: qbState => qbState.salesTermsStatus,
  salesItems: qbState => qbState.salesItems,
  salesItemsStatus: qbState => qbState.salesItemsStatus,
  taxCodes: qbState => qbState.taxCodes,
  taxCodesStatus: qbState => qbState.taxCodesStatus,
};

const mutations = {
  [types.SET_QB_CUSTOMERS](qbState, qbData) {
    Vue.set(qbState.customers, qbData.companyId, qbData.customers);
    Vue.set(qbState.customersStatus, qbData.companyId, qbData.loading);
  },
  [types.SET_QB_ACCOUNTS](qbState, qbData) {
    Vue.set(qbState.accounts, qbData.companyId, qbData.accounts);
    Vue.set(qbState.accountsStatus, qbData.companyId, qbData.loading);
  },
  [types.SET_QB_VENDORS](qbState, qbData) {
    Vue.set(qbState.vendors, qbData.companyId, qbData.vendors);
    Vue.set(qbState.vendorsStatus, qbData.companyId, qbData.loading);
  },
  [types.SET_QB_TERMS](qbState, qbData) {
    Vue.set(qbState.salesTerms, qbData.companyId, qbData.terms);
    Vue.set(qbState.salesTermsStatus, qbData.companyId, qbData.loading);
  },
  [types.SET_QB_SALES_ITEMS](qbState, qbData) {
    Vue.set(qbState.salesItems, qbData.companyId, qbData.items);
    Vue.set(qbState.salesItemsStatus, qbData.companyId, qbData.loading);
  },
  [types.SET_QB_TAX_CODES](qbState, qbData) {
    Vue.set(qbState.taxCodes, qbData.companyId, qbData.items);
    Vue.set(qbState.taxCodesStatus, qbData.companyId, qbData.loading);
  },
  [types.CLEAR_QB_COMPANY_MODULE](qbState, qbData) {
    qbState.customers[qbData.companyId] = null; // eslint-disable-line no-param-reassign
    qbState.accounts[qbData.companyId] = null; // eslint-disable-line no-param-reassign
    qbState.vendors[qbData.companyId] = null; // eslint-disable-line no-param-reassign
    qbState.salesTerms[qbData.companyId] = null; // eslint-disable-line no-param-reassign
    qbState.salesItems[qbData.companyId] = null; // eslint-disable-line no-param-reassign
  },
  [types.CLEAR_QB_MODULE](qbState) {
    qbState.customers = {}; // eslint-disable-line no-param-reassign
    qbState.accounts = {}; // eslint-disable-line no-param-reassign
    qbState.vendors = {}; // eslint-disable-line no-param-reassign
    qbState.salesTerms = {}; // eslint-disable-line no-param-reassign
    qbState.salesItems = {}; // eslint-disable-line no-param-reassign
    qbState.taxCodes = {}; // eslint-disable-line no-param-reassign
  },
};

const actions = {
  clear({ commit }) {
    commit(types.CLEAR_QB_MODULE);
  },
  clearCompany({ commit }, compayId) {
    commit(types.CLEAR_QB_COMPANY_MODULE, { compayId });
  },
  reloadCompanyInfo({ commit, dispatch }, companyId) {
    // here, commit rather than dispatch to ensure completion before loading
    commit(types.CLEAR_QB_COMPANY_MODULE, { companyId });

    dispatch('getQBCustomers', companyId);
    dispatch('getQBAccounts', companyId);
    dispatch('getVendors', companyId);
    dispatch('getTerms', companyId);
    dispatch('getSalesItems', companyId);
    dispatch('getQBTaxCodes', companyId);
  },
  async getQBCustomers({ commit }, companyId) {
    try {
      commit(types.SET_QB_CUSTOMERS, { customers: [], companyId, loading: true });
      const apiResponse = await API.quickBooks.getCustomers(companyId);
      commit(types.SET_QB_CUSTOMERS,
        { customers: apiResponse.data.customers, companyId, loading: false });
    } catch (e) {
      console.error(e.message);
      commit(types.SET_QB_CUSTOMERS, { customers: [], companyId, loading: false });
    }
  },
  async getQBAccounts({ commit }, companyId) {
    try {
      commit(types.SET_QB_ACCOUNTS, { accounts: [], companyId, loading: true });
      const apiResponse = await API.quickBooks.getAccounts(companyId);
      commit(types.SET_QB_ACCOUNTS, {
        accounts: apiResponse.data.accounts, companyId, loading: false
      });
    } catch (e) {
      console.error(e.message);
      commit(types.SET_QB_ACCOUNTS, { accounts: [], companyId, loading: true });
    }
  },
  async getVendors({ commit }, companyId) {
    try {
      commit(types.SET_QB_VENDORS, { vendors: [], companyId, loading: true });
      const apiResponse = await API.quickBooks.getVendors(companyId);
      commit(types.SET_QB_VENDORS, {
        vendors: apiResponse.data.vendors, companyId, loading: false
      });
    } catch (e) {
      console.error(e.message);
      commit(types.SET_QB_VENDORS, { vendors: [], companyId, loading: true });
    }
  },
  async getTerms({ commit }, companyId) {
    try {
      commit(types.SET_QB_TERMS, { terms: [], companyId, loading: true });
      const apiResponse = await API.quickBooks.getSalesTerms(companyId);
      commit(types.SET_QB_TERMS, { terms: apiResponse.data, companyId, loading: false });
    } catch (e) {
      console.error(e.message);
      commit(types.SET_QB_TERMS, { terms: [], companyId, loading: false });
    }
  },
  async getSalesItems({ commit }, companyId) {
    try {
      commit(types.SET_QB_SALES_ITEMS, { items: [], companyId, loading: true });
      const apiResponse = await API.quickBooks.getSalesItems(companyId);
      commit(types.SET_QB_SALES_ITEMS, {
        items: apiResponse.data.items, companyId, loading: false
      });
    } catch (e) {
      console.error(e.message);
      commit(types.SET_QB_SALES_ITEMS, { items: [], companyId, loading: true });
    }
  },
  async getQBTaxCodes({ commit }, companyId) {
    try {
      commit(types.SET_QB_TAX_CODES, { items: [], companyId, loading: true });
      const apiResponse = await API.quickBooks.getTaxCodes(companyId);
      commit(types.SET_QB_TAX_CODES, {
        items: apiResponse.data.taxCodes, companyId, loading: false
      });
    } catch (e) {
      console.error(e.message);
      commit(types.SET_QB_TAX_CODES, { items: [], companyId, loading: true });
    }
  },
};

export default {
  state,
  getters,
  mutations,
  actions,
  namespaced: true,
};



// WEBPACK FOOTER //
// ./src/store/modules/quickbooks.js